import {FormControl, FormGroup} from '@angular/forms';
import {Sorting} from '@brunata-angular/ui';
import {
    caseInsensitiveComparator,
    composeComparators,
    keyComparator,
    proBraLocComparator,
    TranslateService,
} from '@brunata-angular/util';
import {SelectableItem} from '@brunata-angular/ui';
import {DateTime} from 'luxon';
import {MeterConsumption, PercentageMeterConsumption} from '../../consumption-agent-result.model';

export type FormGroupFilter = FormGroup<{
    monitoringPeriodStartDate: FormControl<DateTime | null>;
    monitoringPeriodEndDate: FormControl<DateTime | null>;
    comparisonPeriodStartDate: FormControl<DateTime | null>;
    comparisonPeriodEndDate: FormControl<DateTime | null>;
    proBraLoc: FormControl<string | null>;
    address: FormControl<string | null>;
    placement: FormControl<string | null>;
    meterNo: FormControl<string | null>;
    meterTypeNo: FormControl<SelectableItem[]>;
    alloUnitType: FormControl<SelectableItem[]>;
    unit: FormControl<SelectableItem[]>;
    meterGroupName: FormControl<SelectableItem[]>;
    mountingDate: FormControl<DateTime | null>;
    dismountedDate: FormControl<DateTime | null>;
    replacedBy: FormControl<string | null>;
    monitoringConsumption: FormControl<number | null>;
    diff: FormControl<string | null>;
    diffInPercentage: FormControl<string | null>;
    comparisonConsumption: FormControl<string | null>;
}>;
export type ColumnHeader = {
    label: string;
    column:
        | keyof MeterConsumption
        | keyof PercentageMeterConsumption
        | 'defaultSorting'
        | 'proBraLoc';
    formControl: FormControl<string | number | SelectableItem[] | boolean | DateTime | null>;
    formType: 'text' | 'selectMultiple' | 'dateTime';
    consumptionToMonitor: string[];
    operator: string[];
};
export function getColumnHeads(
    formGroupFilter: FormGroupFilter,
    translateService: TranslateService,
): ColumnHeader[] {
    return [
        {
            label: translateService.translate('properties_proBraLoc', 'online', 'systems'),
            column: 'proBraLoc',
            formControl: formGroupFilter.controls.proBraLoc,
            formType: 'text',
            consumptionToMonitor: [],
            operator: [],
        },
        {
            label: translateService.translate('properties_address', 'online', 'systems'),
            column: 'address',
            formControl: formGroupFilter.controls.address,
            formType: 'text',
            consumptionToMonitor: [],
            operator: [],
        },
        {
            label: translateService.translate('properties_placement', 'online', 'systems'),
            column: 'placement',
            formControl: formGroupFilter.controls.placement,
            formType: 'text',
            consumptionToMonitor: [],
            operator: [],
        },
        {
            label: translateService.translate('properties_meterNo', 'online', 'systems'),
            column: 'meterNo',
            formControl: formGroupFilter.controls.meterNo,
            formType: 'text',
            consumptionToMonitor: [],
            operator: [],
        },
        {
            label: translateService.translate('monitoring_meterType'),
            column: 'meterTypeNo',
            formControl: formGroupFilter.controls.meterTypeNo,
            formType: 'selectMultiple',
            consumptionToMonitor: [],
            operator: [],
        },
        {
            label: translateService.translate('properties_usage', 'online', 'systems'),
            column: 'alloUnitType',
            formControl: formGroupFilter.controls.alloUnitType,
            formType: 'selectMultiple',
            consumptionToMonitor: [],
            operator: [],
        },
        {
            label: translateService.translate('reporting_UNIT'),
            column: 'unit',
            formControl: formGroupFilter.controls.unit,
            formType: 'selectMultiple',
            consumptionToMonitor: [],
            operator: [],
        },
        {
            label: translateService.translate('reporting_GROUP_NAME'),
            column: 'meterGroupName',
            formControl: formGroupFilter.controls.meterGroupName,
            formType: 'selectMultiple',
            consumptionToMonitor: ['DIFFERENCECONSUMPTION'],
            operator: [],
        },
        {
            label: translateService.translate('properties_mountingDate', 'online', 'common'),
            column: 'mountingDate',
            formControl: formGroupFilter.controls.mountingDate,
            formType: 'dateTime',
            consumptionToMonitor: [],
            operator: [],
        },
        {
            label: translateService.translate('utilities_dismount-date'),
            column: 'dismountedDate',
            formControl: formGroupFilter.controls.dismountedDate,
            formType: 'dateTime',
            consumptionToMonitor: [],
            operator: [],
        },
        {
            label: translateService.translate('properties_replacedBy', 'online', 'systems'),
            column: 'replacedBy',
            formControl: formGroupFilter.controls.replacedBy,
            formType: 'text',
            consumptionToMonitor: [],
            operator: [],
        },
        {
            label: translateService.translate('event-alarms_category_Consumption'),
            column: 'monitoringConsumption',
            formControl: formGroupFilter.controls.monitoringConsumption,
            formType: 'text',
            consumptionToMonitor: [],
            operator: [],
        },
        {
            label: `${translateService.translate('event-alarms_category_Consumption')}`,
            column: 'comparisonConsumption',
            formControl: formGroupFilter.controls.comparisonConsumption,
            formType: 'text',
            consumptionToMonitor: [],
            operator: ['INCREASING', 'DECREASING'],
        },
        {
            label: translateService.translate('common_Difference'),
            column: 'diff',
            formControl: formGroupFilter.controls.diff,
            formType: 'text',
            consumptionToMonitor: [],
            operator: ['INCREASING', 'DECREASING'],
        },
        {
            label: translateService.translate('common_differenceInPercentage'),
            column: 'diffInPercentage',
            formControl: formGroupFilter.controls.diffInPercentage,
            formType: 'text',
            consumptionToMonitor: [],
            operator: ['INCREASING', 'DECREASING'],
        },
    ];
}

export function sortMeterConsumptions<T extends MeterConsumption | PercentageMeterConsumption>(
    meterConsumptions: T[],
    sorting: Sorting,
    translateService: TranslateService,
): T[] {
    switch (sorting.column) {
        case 'defaultSorting':
            return meterConsumptions.sort(
                composeComparators([
                    proBraLocComparator(sorting.direction),
                    keyComparator(
                        meter => ('meterNo' in meter ? meter.meterNo : ''),
                        sorting.direction,
                    ),
                    keyComparator(
                        meter => ('mountingDate' in meter ? meter.mountingDate : ''),
                        sorting.direction,
                    ),
                ]),
            );
        case 'proBraLoc':
            return meterConsumptions.sort(proBraLocComparator(sorting.direction));
        case 'meterTypeNo':
            return meterConsumptions.sort(
                caseInsensitiveComparator(
                    meter =>
                        translateService.translate(
                            'mappers_meterType.' + meter.meterTypeNo,
                            'online',
                            'common',
                        ),
                    sorting.direction,
                ),
            );
        case 'alloUnitType':
            return meterConsumptions.sort(
                caseInsensitiveComparator(
                    meter =>
                        meter.alloUnitType != null
                            ? translateService.translate(
                                  'mappers_allocationUnitMap.' + meter.alloUnitType,
                                  'online',
                                  'common',
                              )
                            : '',
                    sorting.direction,
                ),
            );
        case 'meterNo':
            return meterConsumptions.sort(keyComparator(meter => meter.meterNo, sorting.direction));
        case 'mountingDate':
            return meterConsumptions.sort(
                keyComparator(meter => meter.mountingDate?.toISODate() ?? '', sorting.direction),
            );
        case 'dismountedDate':
            return meterConsumptions.sort(
                keyComparator(meter => meter.dismountedDate?.toISODate() ?? '', sorting.direction),
            );
        case 'replacedBy':
            return meterConsumptions.sort(
                caseInsensitiveComparator(meter => meter.replacedBy ?? '', sorting.direction),
            );
        case 'unit':
            return meterConsumptions.sort(
                caseInsensitiveComparator(
                    meter =>
                        translateService.translate(
                            'mappers_measurementUnit.' + meter.unit,
                            'online',
                            'common',
                        ),
                    sorting.direction,
                ),
            );
        case 'meterGroupName':
            return meterConsumptions.sort(
                caseInsensitiveComparator(meter => meter.meterGroupName, sorting.direction),
            );
        case 'address':
            return meterConsumptions.sort(
                caseInsensitiveComparator(meter => meter.address ?? '', sorting.direction),
            );
        case 'placement':
            return meterConsumptions.sort(
                keyComparator(meter => meter.placement ?? '', sorting.direction),
            );
        case 'monitoringConsumption':
            return meterConsumptions.sort(
                keyComparator(it => it.monitoringConsumption, sorting.direction),
            );
        case 'comparisonConsumption':
            return meterConsumptions.sort(
                keyComparator(
                    it => ('comparisonConsumption' in it ? it.comparisonConsumption : null),
                    sorting.direction,
                ),
            );
        case 'diff':
            return meterConsumptions.sort(
                keyComparator(it => ('diff' in it ? it.diff : null), sorting.direction),
            );
        case 'diffInPercentage':
            return meterConsumptions.sort(
                keyComparator(
                    it => ('diffInPercentage' in it ? it.diffInPercentage : null),
                    sorting.direction,
                ),
            );

        default:
            return meterConsumptions;
    }
}
